"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2, ArrowLeft, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { supabase } from "@/lib/supabase";

const formSchema = z.object({
  email: z.string().email("Invalid email address")
});

type FormData = z.infer<typeof formSchema>;

export default function ForgotPassword() {
  const { toast } = useToast();
  const { t } = useTranslation(["auth", "common"]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues
  } = useForm<FormData>({
    resolver: zodResolver(formSchema)
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/login`
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: t("common:status.success"),
        description: t("auth:success.password_reset")
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password reset failed");
      toast({
        title: t("common:status.error"),
        description: "Password reset failed. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - Success message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 bg-gray-50 relative">
          <div className="absolute top-4 right-4">
            <LanguageSwitcher />
          </div>

          <div className="w-full max-w-md bg-white rounded-lg shadow-sm p-6 space-y-6">
            <div className="text-center mb-8">
              <div className="h-12 w-12 rounded-md bg-green-100 flex items-center justify-center text-green-600 mx-auto mb-4">
                <Mail className="h-6 w-6" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("auth:forgot_password.email_sent")}
              </h1>
              <p className="text-gray-600 mt-2">
                {t("auth:forgot_password.check_email")}
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">
                  {t("auth:forgot_password.next_steps")}
                </h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• {t("auth:forgot_password.check_inbox")}</li>
                  <li>• {t("auth:forgot_password.check_spam")}</li>
                  <li>• {t("auth:forgot_password.click_link")}</li>
                  <li>• {t("auth:forgot_password.create_new_password")}</li>
                </ul>
              </div>

              <div className="text-center text-sm text-gray-600">
                {t("auth:forgot_password.sent_to")}{" "}
                <strong>{getValues("email")}</strong>
              </div>

              <Link to="/login">
                <Button className="w-full" variant="outline">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t("auth:forgot_password.back_to_login")}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Right side - Branded background */}
        <div className="hidden lg:flex lg:w-1/2 bg-[#d769b3] items-center justify-center p-8">
          <div className="max-w-md text-white space-y-6">
            <div className="flex items-center gap-2">
              <div className="h-12 w-12 rounded-md bg-[#471999] flex items-center justify-center">
                <span className="text-[#d769b3] font-semibold text-xl">W</span>
              </div>
              <h1 className="text-2xl font-semibold text-[#471999]">
                Workinger
              </h1>
            </div>
            <h2 className="text-4xl font-bold">{t("auth:welcome.title")}</h2>
            <p className="text-lg text-indigo-100">
              {t("auth:welcome.description")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Reset form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 bg-gray-50 relative">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        <div className="w-full max-w-md bg-white rounded-lg shadow-sm p-6 space-y-6">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="h-10 w-10 rounded-md bg-[#d769b3] flex items-center justify-center">
              <span className="text-[#471999] font-semibold">W</span>
            </div>
            <h1 className="text-xl font-semibold">Workinger</h1>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold">
              {t("auth:forgot_password.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("auth:forgot_password.subtitle")}
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">{t("common:form.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("auth:forgot_password.email_placeholder")}
                {...register("email")}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-[#471999] hover:bg-[#471999]/90"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common:status.loading")}
                </>
              ) : (
                t("auth:forgot_password.submit")
              )}
            </Button>
          </form>

          <div className="text-center text-sm">
            <Link
              to="/login"
              className="text-primary hover:underline flex items-center justify-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("auth:forgot_password.back_to_login")}
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - Branded background */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#d769b3] items-center justify-center p-8">
        <div className="max-w-md text-white space-y-6">
          <div className="flex items-center gap-2">
            <div className="h-12 w-12 rounded-md bg-[#471999] flex items-center justify-center">
              <span className="text-[#d769b3] font-semibold text-xl">W</span>
            </div>
            <h1 className="text-2xl font-semibold text-[#471999]">Workinger</h1>
          </div>
          <h2 className="text-4xl font-bold">{t("auth:welcome.title")}</h2>
          <p className="text-lg text-indigo-100">
            {t("auth:welcome.description")}
          </p>
        </div>
      </div>
    </div>
  );
}
